import { type ReactNode } from 'react'
import { type CompanyData, isDefined, isNotDefined, type TeamData, type TeamLevel } from '@terros/common'
import { Select, Text, type ComboboxItem } from '@mantine/core'
import { getLevelName, usePermissionCheck, usePreference, useProfile, useTeams, useTranslation } from '@front/model'
import { type OptionalRowItem } from '../../OptionalRow'
import { useEditTeamField, type TeamFieldInputProps } from './useEditTeamField'
import {
  canMoveTeamToLevel,
  toTeamLevelMoveContext,
  toTeamLevelUpdate,
  type TeamLevelMoveContext,
} from './teamLevelMove'

type Props = {
  team: TeamData
  disabled: boolean
}

const EMPTY_TEAMS: TeamData[] = []

export const useTeamLevelRows = ({ team, disabled }: Props): OptionalRowItem[] => {
  const { t } = useTranslation()
  const { company } = useProfile()
  const { data: managerBasedTeams } = usePreference('managerBasedTeams')
  const { data: canUpdateCompany, isLoading: isCheckingPermission } = usePermissionCheck({
    permissionType: 'company:update',
    targetId: company?.companyId,
  })
  const { data: teams = EMPTY_TEAMS, isLoading: isLoadingTeams } = useTeams({ showArchived: 'all' })
  const highestTeamLevel = company?.highestTeamLevel
  const lowestTeamLevel = company?.lowestTeamLevel
  const hasLevels = isDefined(highestTeamLevel) && isDefined(lowestTeamLevel)
  const context = toTeamLevelMoveContext({ team, teams, highestTeamLevel: highestTeamLevel ?? team.level })

  const renderLevelSelect = ({ label, value, error, onChange }: TeamFieldInputProps): ReactNode => {
    const selectLevel = (next: string | null): void => {
      if (isDefined(next)) onChange(next)
    }
    return (
      <Select
        allowDeselect={false}
        aria-label={label}
        data={toLevelOptions({ context, lowestTeamLevel: lowestTeamLevel ?? team.level, company })}
        error={error}
        flex={1}
        miw={0}
        value={value ?? null}
        onChange={selectLevel}
      />
    )
  }

  const levelEdit = useEditTeamField(team, {
    label: 'Level',
    required: true,
    disabled: disabled || isCheckingPermission || isLoadingTeams || !canUpdateCompany,
    getValue: ({ level }) => String(level),
    toUpdates: (value) => toTeamLevelUpdate(context, Number(value)),
    renderInput: renderLevelSelect,
  })

  if (managerBasedTeams !== false || !hasLevels) return []

  const parent = teams.find((candidate) => candidate.teamId === team.parentId)
  const noParentLabel = team.level === highestTeamLevel ? t('None (top level)') : t('None')
  const parentValue = isNotDefined(team.parentId) ? <Text c='dimmed'>{noParentLabel}</Text> : parent?.name

  return [
    {
      label: t('Level'),
      tooltip: t('Changing the level keeps the parent team. Choosing the top level removes the parent team.'),
      value: getLevelName(company, team.level),
      edit: levelEdit,
    },
    {
      label: t('Parent team'),
      value: parentValue,
    },
  ]
}

type LevelOptionsProps = {
  context: TeamLevelMoveContext
  lowestTeamLevel: TeamLevel
  company: CompanyData | undefined
}

const toLevelOptions = ({ context, lowestTeamLevel, company }: LevelOptionsProps): ComboboxItem[] => {
  const options: ComboboxItem[] = []
  for (let level = context.highestTeamLevel; level >= lowestTeamLevel; level -= 1) {
    options.push({
      value: String(level),
      label: getLevelName(company, level),
      disabled: !canMoveTeamToLevel(context, level),
    })
  }
  return options
}
